import { invokeApi, invokeApiRaw } from '@/lib/apiClient';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ChatHeader } from '@/components/chat/ChatHeader';
import { ChatMessages } from '@/components/chat/ChatMessages';
import { ChatInputBar, ChatInputBarRef } from '@/components/chat/ChatInputBar';
import { EditTitleDialog } from '@/components/chat/EditTitleDialog';
import { DeleteChatDialog } from '@/components/chat/DeleteChatDialog';
import { SecurityAlert } from '@/components/security/SecurityAlert';
import { PlanBanner } from '@/components/chat/PlanBanner';
import { UpgradeModal } from '@/components/chat/UpgradeModal';
import { TopicGeneratorModal } from '@/components/oposiciones/TopicGeneratorModal';
import { useAuth } from '@/hooks/useAuth';
import { useEffectiveMode } from '@/hooks/useEffectiveMode';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  getChatById,
  getChatMessages,
  sendMessage,
  updateChatTitle,
  deleteChat,
  Message,
  Chat,
} from '@/services/chatService';
import {
  compareResolutions,
  summarizeResolutions,
  searchLegalDetailed,
  type LegalResolution,
} from '@/services/legalSearchService';
import { LegalCompareModal } from '@/components/chat/LegalCompareModal';
import { LegalSearchResults } from '@/components/chat/LegalSearchResults';
import { streamChat, formatMessagesForAI } from '@/services/aiService';
import { exportChatToPDF } from '@/services/exportService';
import { uploadChatFile, uploadToAnythingLLM, UploadedFile } from '@/services/fileUploadService';
import { processUploadedFiles } from '@/services/expedienteService';
import { uploadWithConcurrency, getUploadErrorMessage } from '@/services/uploadQueue';
import { useMessageLimit } from '@/hooks/useMessageLimit';
import { AttachedFile } from '@/hooks/useFileExtraction';
import { appModeToChatMode } from '@/lib/chatModeRoutes';
import { normalizeDownloadMode } from '@/services/downloadHistoryService';
import { analyzeExpedienteChronology, ChronologyAnalysisResult } from '@/services/expedienteChronologyService';
import { ChronologyReviewPanel } from '@/components/expedientes/ChronologyReviewPanel';
import { anonymizeText, anonymizeDocumentContent } from '@/services/anonymizationService';
import { CalendarClock, Search, Loader2, Trash2, X, ShieldCheck } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function ChatDetailPage() {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const pendingProcessedRef = useRef(false);

  const [chat, setChat] = useState<Chat | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [editTitleOpen, setEditTitleOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [topicModalOpen, setTopicModalOpen] = useState(false);

  const [securityAlert, setSecurityAlert] = useState<any>(null);
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);

  // Ref for tracking the current streaming message ID
  const streamingIdRef = useRef<string | null>(null);

  // Use effective mode for betatesters
  const mode = useEffectiveMode();
  const isOposiciones = !!(mode && mode.startsWith('oposiciones'));
  const isFuncionarios = mode === 'funcionarios';
  const effectivePlan = isOposiciones ? 'oposiciones' : (profile?.plan || 'free');

  const userPlan = profile?.plan || 'free';
  const { isLimitReached, remaining, refresh: refreshLimit } = useMessageLimit(user?.id, userPlan, mode === 'funcionarios' ? 'funcionarios' : 'opositores');
  const inputBarRef = useRef<ChatInputBarRef>(null);

  const handleAskAboutSelection = (selectedText: string) => {
    inputBarRef.current?.setMessageWithQuote(selectedText);
  };

  const [activeChronologyAnalysis, setActiveChronologyAnalysis] = useState<ChronologyAnalysisResult | null>(null);
  const [activeFileName, setActiveFileName] = useState<string>('');
  const [isAnalyzingChronology, setIsAnalyzingChronology] = useState(false);

  // Anonimización de datos personales (solo modo funcionarios)
  const [isAnonymizationEnabled, setIsAnonymizationEnabled] = useState(() => {
    const stored = localStorage.getItem('munilex_anonymization_enabled');
    return stored === 'true';
  });

  const [selectedResolutions, setSelectedResolutions] = useState<LegalResolution[]>([]);
  const [isLegalSearchOpen, setIsLegalSearchOpen] = useState(false);
  const [legalSources, setLegalSources] = useState<string[]>(['CENDOJ', 'TEAC', 'DGT']);
  const [legalSearchQuery, setLegalSearchQuery] = useState('');
  const [legalSearchResults, setLegalSearchResults] = useState<LegalResolution[]>([]);
  const [legalSearchInfo, setLegalSearchInfo] = useState<{ source?: string; fallbackUsed?: boolean; localCount?: number; ingestedCount?: number } | null>(null);
  const [crawlerErrors, setCrawlerErrors] = useState<string[]>([]);
  const [isLegalSearching, setIsLegalSearching] = useState(false);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [compareModalTitle, setCompareModalTitle] = useState('');
  const [compareModalContent, setCompareModalContent] = useState('');
  const [isComparing, setIsComparing] = useState(false);

  useEffect(() => {
    setSelectedResolutions([]);
    setLegalSearchQuery('');
    setLegalSearchResults([]);
    setLegalSearchInfo(null);
    setCrawlerErrors([]);
  }, [chatId]);

  useEffect(() => {
    if ((location.state as { openLegalSearch?: boolean } | null)?.openLegalSearch) {
      setIsLegalSearchOpen(true);
    }
  }, [location.state]);

  const legalSourceOptions = [
    { value: 'CENDOJ', label: 'CENDOJ' },
    { value: 'TEAC', label: 'TEAC' },
    { value: 'DGT', label: 'DGT' },
    { value: 'JCCA_EXTREMADURA', label: 'Extremadura' },
    { value: 'CCCP_ANDALUCIA', label: 'Andalucía' },
  ];

  const toggleLegalSource = (source: string) => {
    setLegalSources((current) => current.includes(source)
      ? current.filter((item) => item !== source)
      : [...current, source]);
  };

  const handleToggleResolution = (resolution: LegalResolution) => {
    setSelectedResolutions((prev) => {
      const exists = prev.some(
        (r) => r.number === resolution.number && r.type === resolution.type
      );
      if (exists) {
        return prev.filter(
          (r) => !(r.number === resolution.number && r.type === resolution.type)
        );
      }
      return [...prev, resolution];
    });
  };

  const handleCompare = async () => {
    if (selectedResolutions.length === 0) return;
    setCompareModalTitle('Comparativa de Resoluciones');
    setCompareModalContent('');
    setIsComparing(true);
    setCompareModalOpen(true);
    try {
      const comparison = await compareResolutions(selectedResolutions);
      setCompareModalContent(comparison);
    } catch (err: any) {
      console.error(err);
      toast({
        title: 'Error',
        description: 'No se pudo realizar la comparación: ' + err.message,
        variant: 'destructive',
      });
      setCompareModalOpen(false);
    } finally {
      setIsComparing(false);
    }
  };

  const handleSummarize = async () => {
    if (selectedResolutions.length === 0) return;
    setCompareModalTitle('Resumen de Resoluciones');
    setCompareModalContent('');
    setIsComparing(true);
    setCompareModalOpen(true);
    try {
      const summary = await summarizeResolutions(selectedResolutions);
      setCompareModalContent(summary);
    } catch (err: any) {
      console.error(err);
      toast({
        title: 'Error',
        description: 'No se pudo realizar el resumen: ' + err.message,
        variant: 'destructive',
      });
      setCompareModalOpen(false);
    } finally {
      setIsComparing(false);
    }
  };

  const handleLegalSearch = async () => {
    const query = legalSearchQuery.trim();
    if (!query) return;

    setIsLegalSearching(true);
    setCrawlerErrors([]);
    try {
      const response = await searchLegalDetailed(query, {
        sources: legalSources,
        limit: 6,
        forceLive: true,
      });
      setLegalSearchResults(response.results || []);
      setCrawlerErrors(response.crawlerErrors || []);
      setLegalSearchInfo({
        source: response.source,
        fallbackUsed: response.fallbackUsed,
        localCount: response.localCount,
        ingestedCount: response.ingestedCount,
      });
      setSelectedResolutions([]);
      toast({
        title: 'Búsqueda jurídica lista',
        description: response.fallbackUsed
          ? `Se devolvieron ${response.results.length} resultados y se enriqueció el corpus.`
          : `Se devolvieron ${response.results.length} resultados del corpus local.`,
      });
    } catch (err: any) {
      console.error(err);
      toast({
        title: 'Error',
        description: 'No se pudo realizar la búsqueda jurídica: ' + err.message,
        variant: 'destructive',
      });
    } finally {
      setIsLegalSearching(false);
    }
  };

  const handleAnalyzeChronology = async (fileName: string, extractedText: string) => {
    if (!chatId) return;
    setIsAnalyzingChronology(true);
    toast({
      title: 'Analizando cronología',
      description: 'El motor de IA está procesando el expediente...',
    });
    try {
      const result = await analyzeExpedienteChronology({
        fileName,
        text: extractedText,
        chatId,
      });
      setActiveFileName(fileName);
      setActiveChronologyAnalysis(result);
      toast({
        title: 'Análisis de cronología listo',
        description: `Se han detectado ${result.events?.length || 0} hitos y plazos en el documento.`,
      });
    } catch (error) {
      console.error('Error analyzing chronology:', error);
      toast({
        title: 'Error al analizar cronología',
        description: error instanceof Error ? error.message : 'Inténtelo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setIsAnalyzingChronology(false);
    }
  };

  // Validate UUID format
  const isValidUUID = (id: string) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(id);
  };

  useEffect(() => {
    if (chatId && !isValidUUID(chatId)) {
      navigate('/app', { replace: true });
      return;
    }

    setMessages([]);
    setLoading(true);
    setChat(null);

    const initializeChat = async () => {
      if (chatId && user) {
        const loadedChat = await loadChat();
        if (loadedChat) {
          // Check for pending files from ChatHomePage navigation
          const state = location.state as { pendingMessage?: string; pendingFiles?: File[] } | null;
          if (state?.pendingFiles && state.pendingFiles.length > 0 && !pendingProcessedRef.current) {
            pendingProcessedRef.current = true;
            // Clear navigation state to prevent re-processing
            window.history.replaceState({}, '');
            await handlePendingFiles(state.pendingMessage || '', state.pendingFiles, loadedChat);
          } else {
            await loadMessages(undefined, loadedChat);
          }
        }
        checkSecurityAlerts();
      }
    };

    initializeChat();
  }, [chatId, user, navigate]);

  // Handle files passed from ChatHomePage — show message instantly, then process
  const handlePendingFiles = async (content: string, files: File[], chatData: Chat) => {
    if (!chatId || !user) return;

    const messageContent = content.trim() || `${files.length} documento${files.length > 1 ? 's' : ''} adjunto${files.length > 1 ? 's' : ''}`;

    // 1. Show a placeholder user message immediately with file cards
    const placeholderMsg: Message = {
      id: `pending-${Date.now()}`,
      chat_id: chatId,
      role: 'user',
      content: messageContent,
      created_at: new Date().toISOString(),
      metadata: {
        files: files.map(f => ({ name: f.name, size: f.size, type: f.type, url: '', extractedText: '' })),
      },
    };
    setMessages([placeholderMsg]);
    setLoading(false);
    setIsTyping(true);

    const tempStreamingId = `streaming-${Date.now()}`;
    streamingIdRef.current = tempStreamingId;

    try {
      // 2. Upload and extract text with concurrency control (2 at a time)
      const shouldAnonymize = isFuncionarios && isAnonymizationEnabled;
      const uploadResult = await uploadWithConcurrency(
        files,
        async (file) => {
          const isLarge = file.size > 800 * 1024;
          if (isLarge) {
            const { location, workspace, extractedText } = await uploadToAnythingLLM(file, effectivePlan === 'oposiciones' ? 'oposiciones' : 'general', shouldAnonymize);
            return {
              name: file.name,
              size: file.size,
              type: file.type,
              url: '',
              path: '',
              extractedText: extractedText || '[Documento procesado remotamente]',
              remoteLocation: location,
              remoteWorkspace: workspace
            };
          }
          return uploadChatFile(file, user.id, chatId, undefined, shouldAnonymize);
        },
        2,
        (progress) => {
          toast({ title: progress.detail || 'Subiendo archivos...' });
        },
      );

      // Report any failed uploads
      if (uploadResult.failed.length > 0) {
        for (const fail of uploadResult.failed) {
          toast({
            title: 'Error al subir archivo',
            description: getUploadErrorMessage(fail.error, fail.file.name),
            variant: 'destructive',
          });
        }
      }

      if (uploadResult.successful.length === 0) {
        throw new Error('No se pudo subir ningún archivo');
      }

      // 3. Process large expedientes
      const { processedFiles, expedientesProcessed } = await processUploadedFiles(
        uploadResult.successful,
        (stage) => {
          toast({ title: 'Procesando expediente', description: stage });
        }
      );

      if (expedientesProcessed > 0) {
        toast({
          title: 'Expediente procesado',
          description: `${expedientesProcessed} expediente${expedientesProcessed > 1 ? 's' : ''} analizado${expedientesProcessed > 1 ? 's' : ''} con IA`,
        });
      }

      // 4. Truncate and save real message to DB
      const MAX_TEXT_PER_FILE = 200000;
      const MAX_TOTAL_TEXT = 500000;
      let totalText = 0;
      const truncatedFiles = processedFiles.map(f => {
        const rem = MAX_TOTAL_TEXT - totalText;
        if (rem <= 0) return { ...f, extractedText: '' };
        const limit = Math.min(MAX_TEXT_PER_FILE, rem);
        const truncated = f.extractedText.slice(0, limit);
        totalText += truncated.length;
        return { ...f, extractedText: truncated };
      });

      const userMessage = await sendMessage(chatId, messageContent, 'user', { files: truncatedFiles });

      // Replace placeholder with real saved message
      setMessages([userMessage]);

      // 5. Trigger AI response
      let aiMessages = formatMessagesForAI([userMessage]);
      let fileData = processedFiles.map(f => ({
        name: f.name,
        content: f.extractedText,
        type: f.type,
      }));

      // Anonimización de datos personales en modo funcionarios
      if (isFuncionarios && isAnonymizationEnabled) {
        aiMessages = aiMessages.map(msg => ({
          ...msg,
          content: anonymizeText(msg.content).text
        }));
        fileData = fileData.map(f => ({
          ...f,
          content: anonymizeDocumentContent(f.content)
        }));
      }

      const isFirstMessage = chatData.title === 'Nueva consulta' || chatData.title === 'Nueva conversación';
      let aiResponse = '';

      await streamChat({
        messages: aiMessages,
        chatId,
        plan: effectivePlan,
        chatMode: chatData.chat_mode,
        files: fileData.length > 0 ? fileData : undefined,
        onDelta: (delta) => {
          aiResponse += delta;
          setMessages(prev => {
            const existing = prev.find(m => m.id === tempStreamingId);
            if (existing) {
              return prev.map(m => m.id === tempStreamingId ? { ...m, content: aiResponse } : m);
            }
            return [...prev, {
              id: tempStreamingId,
              chat_id: chatId,
              role: 'assistant',
              content: aiResponse,
              created_at: new Date().toISOString(),
            } as Message];
          });
        },
        onDone: async (metadata?: any) => {
          const savedMsg = await sendMessage(chatId, aiResponse, 'assistant', metadata);
          setMessages(prev => prev.map(m => m.id === tempStreamingId ? savedMsg : m));
          streamingIdRef.current = null;
          setIsTyping(false);
          if (isFirstMessage) {
            generateSmartTitle(content, aiResponse);
          }
        },
        onError: (error) => {
          console.error('AI streaming error:', error);
          setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
          streamingIdRef.current = null;
          toast({ title: 'Error', description: error.message || 'No se pudo obtener la respuesta de la IA', variant: 'destructive' });
          setIsTyping(false);
        },
      });
    } catch (error) {
      console.error('Error processing pending files:', error);
      setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
      streamingIdRef.current = null;
      toast({ title: 'Error', description: 'No se pudieron procesar los archivos', variant: 'destructive' });
      setIsTyping(false);
    }
  };

  const loadChat = async () => {
    if (!chatId) return null;

    try {
      const data = await getChatById(chatId);
      setChat(data);
      return data;
    } catch (error) {
      console.error('Error loading chat:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la conversación',
        variant: 'destructive',
      });
      return null;
    }
  };

  const loadMessages = async (before?: string, loadedChat?: Chat | null) => {
    if (!chatId) return;

    try {
      const data = await getChatMessages(chatId, 20, before);

      if (before) {
        setMessages(prev => [...data, ...prev]);
        setIsLoadingMore(false);
      } else {
        setMessages(data);

        // Auto-trigger IA response if last message is from user with no assistant response
        if (data.length > 0 && data[data.length - 1].role === 'user') {
          const chatToUse = loadedChat || chat;
          if (chatToUse) {
            setTimeout(() => {
              triggerAIResponse(data, chatToUse);
            }, 100);
          }
        }
      }

      setHasMore(data.length === 20);
    } catch (error) {
      console.error('Error loading messages:', error);
      setIsLoadingMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = useCallback(() => {
    if (messages.length > 0 && !isLoadingMore) {
      setIsLoadingMore(true);
      const oldestMessage = messages[0];
      loadMessages(oldestMessage.created_at);
    }
  }, [messages, isLoadingMore]);

  const checkSecurityAlerts = async () => {
    if (!user) return;

    try {
      const threeDaysAgo = new Date();
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

      const { data, error } = await supabase
        .from('security_events')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'suspicious_login')
        .gte('timestamp', threeDaysAgo.toISOString())
        .order('timestamp', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0 && !dismissedAlerts.includes(data[0].id)) {
        setSecurityAlert(data[0]);
      }
    } catch (error) {
      console.error('Error checking security alerts:', error);
    }
  };

  // Generate a smart title using AI after first exchange
  const generateSmartTitle = async (userMessage: string, aiResponse: string) => {
    if (!chatId) return;
    try {
      const { data, error } = await invokeApi('generate-title', {
        body: {
          userMessage: userMessage.substring(0, 500),
          aiResponse: aiResponse.substring(0, 500)
        }
      });

      const titleData = data as any;
      if (!error && titleData?.title) {
        await updateChatTitle(chatId, titleData.title);
        setChat(prev => prev ? { ...prev, title: titleData.title } : null);
      }
    } catch {
      // Silent fallback — keep truncated title
      console.log('Smart title generation failed, keeping default');
    }
  };

  const triggerAIResponse = async (currentMessages: Message[], chatData: Chat) => {
    if (!chatId || isTyping) return;

    const lastMessage = currentMessages[currentMessages.length - 1];
    if (!lastMessage || lastMessage.role !== 'user') return;

    console.log('Auto-triggering AI response for first message');
    setIsTyping(true);

    // Generate unique streaming ID
    const tempStreamingId = `streaming-${Date.now()}`;
    streamingIdRef.current = tempStreamingId;

    try {
      const aiMessages = formatMessagesForAI(currentMessages);

      const lastMessageMetadata = lastMessage.metadata as any;
      const fileData = lastMessageMetadata?.files?.map((f: any) => ({
        name: f.name,
        content: f.extractedText,
        type: f.type
      })) || [];

      let aiResponse = '';

      // Check if this is the first user message for title generation
      const userMessagesCount = currentMessages.filter(m => m.role === 'user').length;
      const isFirstMessage = userMessagesCount === 1 &&
        (chatData.title === 'Nueva consulta' || chatData.title === 'Nueva conversación');

      await streamChat({
        messages: aiMessages,
        chatId: chatId,
        plan: effectivePlan,
        chatMode: chatData.chat_mode,
        files: fileData.length > 0 ? fileData : undefined,
        onDelta: (delta) => {
          aiResponse += delta;
          setMessages(prev => {
            const existing = prev.find(m => m.id === tempStreamingId);
            if (existing) {
              return prev.map(m => m.id === tempStreamingId
                ? { ...m, content: aiResponse } : m);
            }
            return [
              ...prev,
              {
                id: tempStreamingId,
                chat_id: chatId,
                role: 'assistant',
                content: aiResponse,
                created_at: new Date().toISOString(),
              } as Message
            ];
          });
        },
        onDone: async (metadata?: any) => {
          const savedMsg = await sendMessage(chatId, aiResponse, 'assistant', metadata);
          // Replace streaming placeholder with saved message
          setMessages(prev => prev.map(m =>
            m.id === tempStreamingId ? savedMsg : m
          ));
          streamingIdRef.current = null;
          setIsTyping(false);

          // Generate smart title after first exchange
          if (isFirstMessage) {
            generateSmartTitle(lastMessage.content, aiResponse);
          }
        },
        onError: (error) => {
          console.error('AI streaming error:', error);
          // Remove streaming placeholder on error
          setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
          streamingIdRef.current = null;
          toast({
            title: 'Error',
            description: error.message || 'No se pudo obtener la respuesta de la IA',
            variant: 'destructive',
          });
          setIsTyping(false);
        }
      });
    } catch (error) {
      console.error('Error triggering AI response:', error);
      setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
      streamingIdRef.current = null;
      setIsTyping(false);
    }
  };

  const handleSendMessage = async (content: string, files?: AttachedFile[]) => {
    if (!chatId || !user) return;
    if (loading) return;

    if (isLimitReached) {
      setUpgradeModalOpen(true);
      return;
    }

    setIsTyping(true);

    // Generate unique streaming ID for this exchange
    const tempStreamingId = `streaming-${Date.now()}`;
    streamingIdRef.current = tempStreamingId;

    try {
      let messageContent = content;
      const uploadedFiles: UploadedFile[] = [];

      // Handle file uploads if present
      if (files && files.length > 0) {
        try {
          // Upload with concurrency control (2 at a time), passing pre-extracted text
          const rawFiles = files.map(af => af.file);
          const shouldAnonymize = isFuncionarios && isAnonymizationEnabled;
          const uploadResult = await uploadWithConcurrency(
            rawFiles,
            (file) => {
              const attached = files.find(af => af.file === file);
              if ((attached as any)?.needsRemoteProcessing) {
                return uploadToAnythingLLM(file, effectivePlan === 'oposiciones' ? 'oposiciones' : 'general', shouldAnonymize).then(res => ({
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  url: '',
                  path: '',
                  extractedText: res.extractedText || '[Documento procesado remotamente]',
                  remoteLocation: res.location,
                  remoteWorkspace: res.workspace
                }));
              }
              return uploadChatFile(file, user.id, chatId, attached?.extractedText, shouldAnonymize);
            },
            2,
            (progress) => {
              toast({ title: progress.detail || 'Subiendo archivos...' });
            },
          );

          // Report failed uploads individually
          if (uploadResult.failed.length > 0) {
            for (const fail of uploadResult.failed) {
              toast({
                title: 'Error al subir archivo',
                description: getUploadErrorMessage(fail.error, fail.file.name),
                variant: 'destructive',
              });
            }
          }

          if (uploadResult.successful.length === 0) {
            toast({ title: 'Error', description: 'No se pudo subir ningún archivo', variant: 'destructive' });
            setIsTyping(false);
            streamingIdRef.current = null;
            return;
          }

          // Process large expedientes through AI segmentation
          const { processedFiles, expedientesProcessed } = await processUploadedFiles(
            uploadResult.successful,
            (stage) => {
              toast({ title: 'Procesando expediente', description: stage });
            }
          );
          uploadedFiles.push(...processedFiles);

          if (expedientesProcessed > 0) {
            toast({
              title: 'Expediente procesado',
              description: `${expedientesProcessed} expediente${expedientesProcessed > 1 ? 's' : ''} analizado${expedientesProcessed > 1 ? 's' : ''} con IA`,
            });
          }

          // Message body = user's typed text only; files go in metadata
          if (!content.trim()) {
            messageContent = `${files.length} documento${files.length > 1 ? 's' : ''} adjunto${files.length > 1 ? 's' : ''}`;
          }
        } catch (error) {
          console.error('Error uploading files:', error);
          toast({
            title: 'Error',
            description: 'No se pudieron subir los archivos',
            variant: 'destructive',
          });
          setIsTyping(false);
          streamingIdRef.current = null;
          return;
        }
      }

      // Truncate extractedText per file to prevent oversized metadata in DB
      const MAX_TEXT_PER_FILE = 200000;
      const MAX_TOTAL_TEXT = 500000;
      let totalText = 0;
      const truncatedFiles = uploadedFiles.map(f => {
        const remaining = MAX_TOTAL_TEXT - totalText;
        if (remaining <= 0) return { ...f, extractedText: '' };
        const limit = Math.min(MAX_TEXT_PER_FILE, remaining);
        const truncated = f.extractedText.slice(0, limit);
        totalText += truncated.length;
        return { ...f, extractedText: truncated };
      });

      // Save user message to DB — content is ONLY what the user typed
      const userMessage = await sendMessage(chatId, messageContent, 'user', truncatedFiles.length > 0 ? { files: truncatedFiles } : undefined);

      // Add user message to state directly (no Realtime dependency)
      setMessages(prev => {
        if (prev.some(m => m.id === userMessage.id)) return prev;
        return [...prev, userMessage];
      });

      // Check if this is the first user message for smart title generation
      const userMessagesCount = messages.filter(m => m.role === 'user').length;
      const isFirstMessage = userMessagesCount === 0 &&
        (chat?.title === 'Nueva consulta' || chat?.title === 'Nueva conversación');

      // Prepare messages for AI (filter by current chatId to prevent cross-chat leaks)
      const currentMessages = messages.filter(m => m.chat_id === chatId);
      let aiMessages = formatMessagesForAI([...currentMessages, userMessage]);

      // Prepare file data to send to AI
      let fileData = uploadedFiles.map(f => ({
        name: f.name,
        content: f.extractedText,
        type: f.type
      }));

      // Anonimización de datos personales en modo funcionarios
      if (isFuncionarios && isAnonymizationEnabled) {
        aiMessages = aiMessages.map(msg => ({
          ...msg,
          content: anonymizeText(msg.content).text
        }));
        fileData = fileData.map(f => ({
          ...f,
          content: anonymizeDocumentContent(f.content)
        }));
      }

      // Stream AI response with unique ID
      let aiResponse = '';

      await streamChat({
        messages: aiMessages,
        chatId: chatId,
        plan: effectivePlan,
        chatMode: chat?.chat_mode || appModeToChatMode(mode),
        files: fileData.length > 0 ? fileData : undefined,
        onDelta: (delta) => {
          aiResponse += delta;
          setMessages(prev => {
            const existing = prev.find(m => m.id === tempStreamingId);
            if (existing) {
              return prev.map(m => m.id === tempStreamingId
                ? { ...m, content: aiResponse } : m);
            }
            return [
              ...prev,
              {
                id: tempStreamingId,
                chat_id: chatId,
                role: 'assistant',
                content: aiResponse,
                created_at: new Date().toISOString(),
              } as Message
            ];
          });
        },
        onDone: async (metadata?: any) => {
          const savedMsg = await sendMessage(chatId, aiResponse, 'assistant', metadata);
          // Replace streaming placeholder with real saved message
          setMessages(prev => prev.map(m =>
            m.id === tempStreamingId ? savedMsg : m
          ));
          streamingIdRef.current = null;
          setIsTyping(false);

          // Generate smart title after first exchange
          if (isFirstMessage) {
            generateSmartTitle(content, aiResponse);
          }
        },
        onError: (error) => {
          console.error('AI streaming error:', error);
          // Remove streaming placeholder on error
          setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
          streamingIdRef.current = null;
          toast({
            title: 'Error',
            description: error.message || 'No se pudo obtener la respuesta de la IA',
            variant: 'destructive',
          });
          setIsTyping(false);
        }
      });

    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
      streamingIdRef.current = null;
      toast({
        title: 'Error',
        description: 'No se pudo enviar el mensaje',
        variant: 'destructive',
      });
      setIsTyping(false);
    }
  };

  const handleEditTitle = async (newTitle: string) => {
    if (!chatId) return;

    try {
      await updateChatTitle(chatId, newTitle);
      toast({
        title: 'Título actualizado',
        description: 'El título de la conversación se ha actualizado correctamente',
      });
      loadChat();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el título',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteChat = async () => {
    if (!chatId) return;

    try {
      await deleteChat(chatId);
      toast({
        title: 'Conversación eliminada',
        description: 'La conversación se ha eliminado correctamente',
      });
      navigate('/app');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la conversación',
        variant: 'destructive',
      });
    }
  };

  const handleDismissAlert = () => {
    if (securityAlert) {
      setDismissedAlerts([...dismissedAlerts, securityAlert.id]);
      setSecurityAlert(null);
    }
  };

  const handleRegenerate = async () => {
    if (!chatId || isTyping || messages.length < 2) return;

    const lastUserMessageIndex = [...messages].reverse().findIndex(m => m.role === 'user');
    if (lastUserMessageIndex === -1) return;

    const userMessageIndex = messages.length - 1 - lastUserMessageIndex;
    const lastUserMessage = messages[userMessageIndex];

    // Remove the last assistant message from UI
    setMessages(prev => prev.filter((_, i) => i < prev.length - 1));
    setIsTyping(true);

    // Generate unique streaming ID for regeneration
    const tempStreamingId = `streaming-regen-${Date.now()}`;
    streamingIdRef.current = tempStreamingId;

    try {
      // Delete the last assistant message from database
      const lastAssistantMessage = messages[messages.length - 1];
      if (lastAssistantMessage && lastAssistantMessage.role === 'assistant' && lastAssistantMessage.id && !lastAssistantMessage.id.startsWith('streaming-')) {
        await supabase
          .from('messages')
          .delete()
          .eq('id', lastAssistantMessage.id);
      }

      const messagesForAI = messages.slice(0, userMessageIndex + 1);
      const aiMessages = formatMessagesForAI(messagesForAI);

      const lastMessageMetadata = lastUserMessage.metadata as any;
      const fileData = lastMessageMetadata?.files?.map((f: any) => ({
        name: f.name,
        content: f.extractedText,
        type: f.type
      })) || [];

      let aiResponse = '';

      await streamChat({
        messages: aiMessages,
        chatId: chatId,
        plan: effectivePlan,
        chatMode: chat?.chat_mode || appModeToChatMode(mode),
        files: fileData.length > 0 ? fileData : undefined,
        onDelta: (delta) => {
          aiResponse += delta;
          setMessages(prev => {
            const existing = prev.find(m => m.id === tempStreamingId);
            if (existing) {
              return prev.map(m => m.id === tempStreamingId
                ? { ...m, content: aiResponse } : m);
            }
            return [
              ...prev,
              {
                id: tempStreamingId,
                chat_id: chatId,
                role: 'assistant',
                content: aiResponse,
                created_at: new Date().toISOString(),
              } as Message
            ];
          });
        },
        onDone: async (metadata?: any) => {
          const savedMsg = await sendMessage(chatId, aiResponse, 'assistant', metadata);
          setMessages(prev => prev.map(m =>
            m.id === tempStreamingId ? savedMsg : m
          ));
          streamingIdRef.current = null;
          setIsTyping(false);
        },
        onError: (error) => {
          console.error('AI regeneration error:', error);
          setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
          streamingIdRef.current = null;
          toast({
            title: 'Error',
            description: error.message || 'No se pudo regenerar la respuesta',
            variant: 'destructive',
          });
          setIsTyping(false);
        }
      });
    } catch (error) {
      console.error('Error regenerating response:', error);
      setMessages(prev => prev.filter(m => m.id !== tempStreamingId));
      streamingIdRef.current = null;
      toast({
        title: 'Error',
        description: 'No se pudo regenerar la respuesta',
        variant: 'destructive',
      });
      setIsTyping(false);
      loadMessages();
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full items-center justify-center bg-white">
        <p className="text-muted-foreground">Cargando conversación...</p>
      </div>
    );
  }

  if (!chat) {
    return (
      <div className="flex flex-col h-full bg-white">
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <div className="text-center max-w-xl space-y-4">
            <h1 className="text-2xl font-semibold text-[#002B5B]">
              Empecemos, ¿qué consulta jurídica necesitas revisar?
            </h1>
            <p className="text-base text-[#6B7280]">
              Estoy aquí para ayudarte con tus expedientes, dictámenes y normativa municipal.
            </p>
          </div>
        </div>
        <ChatInputBar onSendMessage={handleSendMessage} disabled={true} />
      </div>
    );
  }

  const handleExportPDF = async () => {
    if (!chat) return;
    try {
      await exportChatToPDF(chat, messages);
      // Register download
      if (user) {
        const { saveDownload } = await import('@/services/downloadHistoryService');
        saveDownload({
          userId: user.id,
          title: chat.title || 'Conversación',
          downloadType: 'chat_export',
          mode: normalizeDownloadMode(mode),
          metadata: { chat_id: chatId, chat_title: chat.title },
        });
      }
      toast({
        title: 'PDF exportado',
        description: 'La conversación se ha descargado como PDF.',
      });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar la conversación.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div className="flex flex-col h-full bg-white relative">
        <ChatHeader
          title={chat?.title || '(Sin título)'}
          onEditTitle={() => setEditTitleOpen(true)}
          onDeleteChat={() => setDeleteDialogOpen(true)}
          onExportPDF={handleExportPDF}
          userPlan={userPlan}
          chatMode={chat?.chat_mode}
        />

        {securityAlert && (
          <SecurityAlert event={securityAlert} onDismiss={handleDismissAlert} />
        )}

        <div className="px-4 pt-3 pb-2">
          <PlanBanner plan={effectivePlan} mode={mode === 'funcionarios' ? 'funcionarios' : 'opositores'} />
        </div>

        {isFuncionarios && isLegalSearchOpen && (
          <div className="px-4 pb-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Search className="h-4 w-4" />
                    Buscador jurisprudencial
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Corpus local de CENDOJ, DGT y TEAC con fallback en vivo cuando falten datos.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  {legalSearchInfo?.source && (
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-1">
                      {legalSearchInfo.source}
                    </span>
                  )}
                  {typeof legalSearchInfo?.localCount === 'number' && (
                    <span className="rounded-full border border-slate-200 bg-white px-2 py-1">
                      {legalSearchInfo.localCount} locales
                    </span>
                  )}
                  {legalSearchInfo?.fallbackUsed ? (
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-amber-700">
                      fallback activo
                    </span>
                  ) : null}
                </div>
              </div>

              <div className="mt-3 flex flex-col gap-2 md:flex-row">
                <Input
                  value={legalSearchQuery}
                  onChange={(e) => setLegalSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLegalSearch();
                    }
                  }}
                  placeholder="Ej.: compensación de tributos, interés de demora, plusvalía, subvenciones..."
                  className="bg-white"
                />
                <Button
                  onClick={handleLegalSearch}
                  disabled={isLegalSearching || !legalSearchQuery.trim() || legalSources.length === 0}
                  className="gap-2"
                >
                  {isLegalSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Buscar
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsLegalSearchOpen(false)}
                  aria-label="Cerrar buscador jurídico"
                  title="Cerrar buscador jurídico"
                >
                  <X className="h-4 w-4" />
                </Button>
                {legalSearchResults.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setLegalSearchQuery('');
                      setLegalSearchResults([]);
                      setLegalSearchInfo(null);
                      setSelectedResolutions([]);
                    }}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Limpiar
                  </Button>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-2" aria-label="Fuentes jurídicas">
                {legalSourceOptions.map((source) => {
                  const selected = legalSources.includes(source.value);
                  return (
                    <button
                      key={source.value}
                      type="button"
                      onClick={() => toggleLegalSource(source.value)}
                      className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        selected
                          ? 'border-[#002B5B] bg-[#002B5B] text-white'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-[#002B5B]/50'
                      }`}
                      aria-pressed={selected}
                    >
                      {source.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <LegalCompareModal
          open={compareModalOpen}
          onOpenChange={setCompareModalOpen}
          title={compareModalTitle}
          content={compareModalContent}
          isLoading={isComparing}
        />

        {selectedResolutions.length > 0 && (
          <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-background/90 backdrop-blur-md border border-border shadow-lg px-4 py-3 rounded-xl flex items-center gap-4 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <span className="text-xs font-semibold text-[#002B5B] dark:text-foreground">
              {selectedResolutions.length} seleccionadas
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCompare}
                className="px-3 py-1.5 bg-[#002B5B] text-white text-xs font-semibold rounded-lg hover:bg-[#002B5B]/90 transition-all shadow-sm flex items-center gap-1.5"
              >
                Comparar
              </button>
              <button
                onClick={handleSummarize}
                className="px-3 py-1.5 bg-[#06D3AA] text-[#002B5B] text-xs font-semibold rounded-lg hover:bg-[#05b897] transition-all shadow-sm flex items-center gap-1.5"
              >
                Resumir
              </button>
              <button
                onClick={() => setSelectedResolutions([])}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1.5"
              >
                Limpiar
              </button>
            </div>
          </div>
        )}

        {activeChronologyAnalysis && (
          <div className="px-4 pb-3 border-t border-amber-100 bg-amber-50/30">
            <ChronologyReviewPanel
              analysis={activeChronologyAnalysis}
              chatId={chatId || ''}
              fileName={activeFileName}
              onClose={() => setActiveChronologyAnalysis(null)}
            />
          </div>
        )}

        {(isOposiciones) && messages.length === 0 && (
          <div className="px-4 pb-2">
            <button
              onClick={() => setTopicModalOpen(true)}
              className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg border border-violet-200 bg-violet-50/50 hover:bg-violet-100 text-violet-700 text-sm font-medium transition-colors"
            >
              ✨ Generar material de estudio (Presentación / Resumen / Infografía)
            </button>
          </div>
        )}

        <ChatMessages
          messages={messages}
          isTyping={isTyping}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMore}
          onAskAboutSelection={handleAskAboutSelection}
          onAnalyzeChronology={handleAnalyzeChronology}
          selectedResolutions={selectedResolutions}
          onToggleSelect={handleToggleResolution}
          onRegenerate={handleRegenerate}
          chatId={chatId}
          chatTitle={chat?.title || undefined}
          mode={mode as any}
        />

        {isFuncionarios && isAnonymizationEnabled && (
          <div className="px-4 py-2 bg-emerald-50 border-t border-emerald-100">
            <div className="max-w-4xl mx-auto flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span className="text-xs text-emerald-700">
                Anonimización activada: los datos personales (DNI, emails, teléfonos, direcciones) se redactarán automáticamente antes de enviar al asistente (LOPDGDD/RGPD).
              </span>
            </div>
          </div>
        )}

        <ChatInputBar
          key={chatId}
          ref={inputBarRef}
          onSendMessage={handleSendMessage}
          onLegalSearch={() => setIsLegalSearchOpen(true)}
          disabled={isTyping}
          isLimitReached={isLimitReached}
          remaining={remaining}
          onUpgradeClick={() => setUpgradeModalOpen(true)}
          variant={isOposiciones ? 'oposiciones' : 'funcionarios'}
          anonymizationEnabled={isAnonymizationEnabled}
          onToggleAnonymization={() => {
            const next = !isAnonymizationEnabled;
            setIsAnonymizationEnabled(next);
            localStorage.setItem('munilex_anonymization_enabled', String(next));
          }}
          showAnonymizationToggle={isFuncionarios}
        />

        {isAnalyzingChronology && (
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center animate-fade-in">
            <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center max-w-sm mx-4 text-center border border-amber-100">
              <div className="relative mb-4">
                <div className="h-16 w-16 rounded-full border-4 border-amber-100 border-t-amber-500 animate-spin flex items-center justify-center" />
                <CalendarClock className="h-6 w-6 text-amber-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-bounce" />
              </div>
              <h3 className="font-semibold text-slate-800 text-lg">Analizando Expediente</h3>
              <p className="text-sm text-slate-500 mt-2">
                Nuestra Inteligencia Artificial está procesando el documento para detectar hitos y plazos clave...
              </p>
            </div>
          </div>
        )}
      </div>

      <TopicGeneratorModal
        open={topicModalOpen}
        onOpenChange={setTopicModalOpen}
        variant={
          mode === 'oposiciones_c1' ? 'c1'
          : mode === 'oposiciones_iipp' ? 'iipp'
          : mode === 'oposiciones_guardia_civil' ? 'guardia_civil'
          : mode === 'oposiciones_policia_nacional' ? 'policia_nacional'
          : mode === 'oposiciones_c2' ? 'c2'
          : 'secretaria'
        }
      />

      <EditTitleDialog
        open={editTitleOpen}
        currentTitle={chat?.title || ''}
        onOpenChange={setEditTitleOpen}
        onSave={handleEditTitle}
      />

      <DeleteChatDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteChat}
      />

      <UpgradeModal
        open={upgradeModalOpen}
        onOpenChange={setUpgradeModalOpen}
      />
    </>
  );
}
